/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/pages/ReportDetailPage.js
 * 📌 프로그램명: AI 건강 리포트 상세 페이지
 * 🧩 개요:
 * - 사용자의 특정 건강 분석 리포트 상세 내용을 보여주는 웹 전용 페이지 컴포넌트입니다.
 * - 공통 컨트롤러로 데이터/오디오/피드백 상태를 관리하고, 웹 화면 레이아웃만 담당합니다.
 * ===================================================================================
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import MessageModal from '../components/MessageModal';
import useReportDetailController from '../features/report-detail/useReportDetailController';
import { markdownSanitizeOptions } from '../utils/markdownSanitizeOptions';

function ReportDetailPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const {
    report,
    isLoading,
    reportErrorMessage,
    isReportErrorModalOpen,
    setIsReportErrorModalOpen,
    feedbackSubmitting,
    handleFeedback,
    feedbackError,
    hasFeedback,
    effectiveRating,
    effectiveDate,
    audioLoading,
    handleAudioOverviewClick,
    audioStatusLabel,
    audioState,
    audioError,
    audioUrl,
    audioPlayerRef,
    audioSectionRef,
    createdAtLabel,
    navigateToActionPlan,
  } = useReportDetailController({ reportId, isApp: false });

  if (isLoading) {
    return <div style={{ padding: 16 }}>리포트를 불러오는 중입니다...</div>;
  }

  if (!report) {
    return (
      <MessageModal
        isOpen={isReportErrorModalOpen}
        onClose={() => setIsReportErrorModalOpen(false)}
        title="알림"
        message={reportErrorMessage}
        type="error"
      />
    );
  }

  return (
    <div style={{ padding: 16, fontFamily: 'sans-serif', lineHeight: 1.6, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 800 }}>
        <div className="flex-center-between">
          <h2 className="page-title">분석 리포트</h2>
          <div className="report-header-actions">
            <button onClick={() => navigate(-1)} className="btn btn-outline">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              <span>뒤로</span>
            </button>
            <button
              onClick={handleAudioOverviewClick}
              className="btn audio-brief-btn"
              style={{
                background: '#f0f7ff',
                border: '1px solid #dbeafe',
                color: '#1f2937',
                minWidth: 120,
              }}
              disabled={audioLoading}
            >
              {audioLoading ? '오디오 준비중...' : '오디오 브리핑'}
            </button>
            <button onClick={navigateToActionPlan} className="btn btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
              <span>실천계획</span>
            </button>
          </div>
        </div>

        {createdAtLabel && (
          <div style={{ color: '#666', fontSize: 12, marginTop: 6 }}>생성일: {createdAtLabel}</div>
        )}

        {report.recommendation_overview && (
          <div style={{ background: '#f0f7ff', padding: '12px 16px', borderRadius: '10px', marginTop: 12, marginBottom: '24px', border: '1px solid #dbeafe' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: '#e0e7ff', color: '#3730a3' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M7 13l3 3 7-7" /></svg>
              </span>
              <h3 style={{ margin: 0, color: '#1f2937' }}>한눈에 보는 요약</h3>
            </div>
            <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-line' }}>{report.recommendation_overview}</p>
          </div>
        )}

        {(audioLoading || audioStatusLabel || audioError || audioUrl) && (
          <div
            ref={audioSectionRef}
            style={{
              background: '#f8fafc',
              padding: '16px',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              marginBottom: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: '#fee2e2', color: '#b91c1c' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19V5" /><path d="M5 12h14" /></svg>
              </span>
              <h3 style={{ margin: 0, color: '#0f172a' }}>오디오 브리핑</h3>
            </div>
            {audioStatusLabel && (
              <p style={{ margin: '12px 0 0 0', color: '#1f2937', whiteSpace: 'pre-line' }}>
                <strong>{audioStatusLabel}</strong>
                {audioState.message ? ` ${audioState.message}` : ''}
              </p>
            )}
            {audioLoading && !audioError && (
              <p style={{ margin: '8px 0 0 0', color: '#475569', fontSize: 13 }}>
                오디오를 준비 중입니다. {audioState.retryAfter ? `${audioState.retryAfter}초 후 상태를 다시 확인합니다.` : '잠시만 기다려 주세요.'}
              </p>
            )}
            {audioError && (
              <p style={{ margin: '12px 0 0 0', color: '#dc2626', fontSize: 13 }}>{audioError}</p>
            )}
            {audioUrl && !audioError && (
              <audio ref={audioPlayerRef} controls src={audioUrl} style={{ width: '100%', marginTop: 12 }}>
                브라우저에서 오디오 태그를 지원하지 않습니다.
              </audio>
            )}
          </div>
        )}

        {report.report_text && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: '#dcfce7', color: '#065f46' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v16H4z" /><path d="M8 8h8M8 12h8M8 16h6" /></svg>
              </span>
              <h3 style={{ margin: 0 }}>AI 상세 분석 결과</h3>
            </div>
            <div className="markdown-body" style={{ border: '1px solid #dbe2eb', padding: '16px', borderRadius: '8px', backgroundColor: '#f5f7fa' }}>
              <ReactMarkdown rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeOptions]]}>
                {report.report_text}
              </ReactMarkdown>
            </div>
          </div>
        )}

        <div className="feedback-section" style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee' }}>
          {!hasFeedback ? (
            <>
              <p style={{ textAlign: 'center', marginBottom: 12 }}>이 분석이 도움이 되셨나요?</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  className="feedback-button"
                  onClick={() => handleFeedback('helpful')}
                  disabled={feedbackSubmitting}
                  title={undefined}
                >
                  👍 도움이 되었어요
                </button>
                <button
                  className="feedback-button"
                  onClick={() => handleFeedback('needs_improvement')}
                  disabled={feedbackSubmitting}
                  title={undefined}
                >
                  👎 개선이 필요해요
                </button>
              </div>
            </>
          ) : (
            <div className="feedback-message" style={{ width: '100%', textAlign: 'center', marginTop: 8 }}>
              <span className="submitted-badge" style={{ fontSize: 12, color: '#555' }}>
                {effectiveRating === 'helpful'
                  ? '도움이 되셨다니 다행이네요. 감사합니다.'
                  : '개선이 필요하다는 의견 감사합니다. 도움이 될 수 있도록 더욱 노력하겠습니다.'}
                {effectiveDate && (
                  <>
                    <br />
                    <span style={{ fontSize: 11, color: '#777' }}>{new Date(effectiveDate).toLocaleString()}</span>
                  </>
                )}
              </span>
            </div>
          )}
          {feedbackError && (
            <div style={{ width: '100%', textAlign: 'center', marginTop: 8 }}>
              <span className="error-text" style={{ color: '#c00', fontSize: 12 }}>
                {feedbackError}
              </span>
            </div>
          )}
        </div>

        <MessageModal
          isOpen={isReportErrorModalOpen}
          onClose={() => setIsReportErrorModalOpen(false)}
          title="알림"
          message={reportErrorMessage}
          type="error"
        />
      </div>
    </div>
  );
}

export default ReportDetailPage;
